import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { num } from "@/lib/format";
import { Home, Search, Save, Loader2, MapPin, Lock } from "lucide-react";

export default function BaseConfigCard({ droneId, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("ARDUPILOT_HOME");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState(null); // {lat,lng,display}
  const [geocoding, setGeocoding] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/drones/${droneId}/config`);
      setCfg(data.config);
      setMode(data.config.base_mode);
      if (data.config.base_lat != null) {
        setCoords({ lat: data.config.base_lat, lng: data.config.base_lng, display: data.config.base_address });
        setAddress(data.config.base_address || "");
      } else { setCoords(null); setAddress(""); }
    } catch (e) { setCfg(null); setCoords(null); setAddress(""); }
  }, [droneId]);

  useEffect(() => { load(); }, [load]);

  const geocode = async () => {
    if (!address.trim()) return;
    setGeocoding(true);
    try {
      const { data } = await api.get("/geocode", { params: { q: address } });
      setCoords({ lat: data.lat, lng: data.lng, display: data.display_name });
      toast.success("Adresse géocodée", { description: data.display_name });
    } catch (e) { toast.error("Géocodage échoué", { description: apiErr(e) }); }
    finally { setGeocoding(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { base_mode: mode };
      if (mode === "CUSTOM_BASE") {
        if (!coords) { toast.error("Géocodez une adresse d'abord"); setSaving(false); return; }
        body.base_address = coords.display; body.base_lat = coords.lat; body.base_lng = coords.lng;
      }
      const { data } = await api.put(`/drones/${droneId}/config`, body);
      setCfg(data.config);
      toast.success("Base enregistrée");
      onChanged?.();
    } catch (e) { toast.error("Échec", { description: apiErr(e) }); }
    finally { setSaving(false); }
  };

  if (!cfg) return null;
  const hasHome = cfg.ardupilot_home_lat != null;

  return (
    <div data-testid="base-config-card" className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 space-y-3">
      <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#525252] flex items-center gap-1.5">
        <Home size={12} /> Base / Home {!isAdmin && <Lock size={10} className="ml-auto text-[#525252]" />}
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        {[["ARDUPILOT_HOME", "Home ArduPilot"], ["CUSTOM_BASE", "Base par adresse"]].map(([v, l]) => (
          <button key={v} data-testid={`base-mode-${v}`} disabled={!isAdmin} onClick={() => setMode(v)}
            className={`font-mono text-[10px] uppercase tracking-wider rounded-sm px-2 py-1.5 border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
              mode === v ? "border-[#3B82F6] text-[#3B82F6] bg-[#3B82F6]/10" : "border-white/15 text-[#A3A3A3] hover:bg-white/5"}`}>
            {l}
          </button>
        ))}
      </div>

      {mode === "ARDUPILOT_HOME" ? (
        <div className="font-mono text-[10px] text-[#A3A3A3] space-y-1">
          <p className="text-[#F59E0B]/90 leading-relaxed">La base est définie automatiquement par ArduPilot lorsque le GPS est prêt et que le drone initialise sa position Home.</p>
          <div className="bg-[#141414] border border-white/10 rounded-sm px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wider text-[#525252]">Dernière Home connue</div>
            {hasHome ? (
              <div data-testid="ardupilot-home" className="text-white">{num(cfg.ardupilot_home_lat, 5)}, {num(cfg.ardupilot_home_lng, 5)}</div>
            ) : <div className="text-[#525252]">Non transmise par MAVLink</div>}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-1.5">
            <input data-testid="base-address-input" value={address} onChange={(e) => setAddress(e.target.value)} disabled={!isAdmin}
              onKeyDown={(e) => e.key === "Enter" && geocode()} placeholder="Adresse de la base"
              className="flex-1 bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-2 py-1.5 font-mono text-xs disabled:opacity-60" />
            <button data-testid="base-geocode-btn" onClick={geocode} disabled={!isAdmin || geocoding}
              className="flex items-center gap-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-sm px-2.5 font-mono text-[10px] uppercase disabled:opacity-50">
              {geocoding ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            </button>
          </div>
          {coords && (
            <div data-testid="base-coords" className="font-mono text-[10px] text-[#A3A3A3] flex items-center gap-1">
              <MapPin size={11} className="text-[#F59E0B]" /> {num(coords.lat, 5)}, {num(coords.lng, 5)}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <button data-testid="base-save-btn" onClick={save} disabled={saving}
          className="w-full flex items-center justify-center gap-2 border border-white/20 hover:bg-white/10 rounded-sm font-mono text-[11px] uppercase tracking-wider py-2 disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer base
        </button>
      )}
    </div>
  );
}
